import { z } from "zod";
// Reuse the canonical Json schema from the shared barrel's first module so
// `export *` from index.ts has exactly one `jsonSchema` symbol.
import { jsonSchema } from "./service-requests";

/**
 * Tasks shared contract (Strangler-Fig migration, manifest-6 / Track A7).
 *
 * Single source of truth for the `tasks` / `task_activity` / `task_type_costs` /
 * `visits` wire shapes shared by the web SPA (`src/repositories/tasks.ts`) and
 * the NestJS slice (`apps/api/src/tasks/*`). Every shape is **snake_case** so the
 * frontend<->Nest transport is a field-for-field swap (no renames), matching the
 * Supabase generated Rows and the Nest repository `.select()` lists.
 *
 * The web repo `.parse()`s Nest responses against these schemas at the staff flip
 * boundary, so any backend/frontend drift surfaces at runtime (and the repo falls
 * back to the Supabase path).
 *
 * ⚠️ TOLERANCE RULE (the #1699 silent-no-op lesson): the kanban/detail reads embed
 * nested relations (`employee`, `property`, `unit`, `service_request`, `actor`)
 * whose column sets vary and may be `null`. So relation fields are
 * `.optional()` + `.nullable()` with a **passthrough** shape — a too-strict
 * relation schema would make every Nest call silently fall back to Supabase. Only
 * each row's own columns are pinned (loosely — many task columns are additive,
 * migration-gated, and absent from older rows). The whole object is
 * `.passthrough()` so a new migration column never rejects a valid payload.
 */

/** Loose, passthrough relation shape — see the tolerance rule above. */
const relation = z.record(jsonSchema).nullable().optional();

/**
 * A persisted task row as the dashboard kanban / detail screens consume it. The
 * own-columns set + nullability mirror the Supabase generated `tasks` Row. Many
 * columns are additive (assessment codes, due_time, payout_amount_override,
 * inspection_report) and absent from older rows — they are `.optional()` so a
 * pre-migration row still parses. `.passthrough()` keeps unmodelled columns.
 */
export const taskSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    status: z.string(),
    priority: z.string(),
    due_date: z.string().nullable(),
    due_time: z.string().nullable().optional(),
    payout_amount_override: z.number().nullable().optional(),
    assigned_to: z.string().nullable(),
    property_id: z.string().nullable(),
    unit_id: z.string().nullable(),
    contract_id: z.string().nullable(),
    listing_id: z.string().nullable(),
    visit_id: z.string().nullable().optional(),
    service_request_id: z.string().nullable().optional(),
    task_type: z.string().nullable(),
    visitor_name: z.string().nullable(),
    visitor_phone: z.string().nullable(),
    visitor_email: z.string().nullable(),
    preferred_visit_time: z.string().nullable(),
    on_hold_reason: z.string().nullable().optional(),
    created_by: z.string().nullable(),
    completed_at: z.string().nullable(),
    notes: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    // Joined / hydrated relations — loose + optional + nullable.
    employee: relation,
    property: relation,
    unit: relation,
    contract: relation,
    service_request: relation,
  })
  .passthrough();

export type Task = z.infer<typeof taskSchema>;

/**
 * A `task_activity` audit row. Append-only — there is no update/delete shape.
 * `task_activity` carries no own `company_id` column; scope is enforced through
 * the parent task. `actor` is a hydrated profile relation (loose).
 */
export const taskActivitySchema = z
  .object({
    id: z.string(),
    task_id: z.string(),
    actor_id: z.string().nullable(),
    kind: z.string(),
    payload: jsonSchema.nullable(),
    created_at: z.string(),
    actor: relation,
  })
  .passthrough();

export type TaskActivity = z.infer<typeof taskActivitySchema>;

/** A `task_type_costs` row — per (company_id, task_type) payout cost. */
export const taskTypeCostSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    task_type: z.string(),
    amount: z.number(),
    currency: z.string(),
    is_active: z.boolean(),
    note: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough();

export type TaskTypeCost = z.infer<typeof taskTypeCostSchema>;

/** A `visits` row — a scheduled property viewing. */
export const visitSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    property_id: z.string(),
    unit_id: z.string().nullable(),
    task_id: z.string().nullable().optional(),
    visitor_name: z.string(),
    visitor_phone: z.string(),
    visit_date: z.string(),
    visit_time: z.string(),
    status: z.string(),
    notes: z.string().nullable(),
    interest_level: z.string().nullable(),
    feedback_comments: z.string().nullable(),
    follow_up_needed: z.boolean().nullable(),
    feedback_submitted_at: z.string().nullable(),
    feedback_submitted_by: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough();

export type Visit = z.infer<typeof visitSchema>;

/**
 * The PUBLIC, unauthenticated token lookup result (mirrors the
 * `get_service_request_by_token` RPC Return row). No company scope — the token
 * itself is the capability.
 */
export const serviceRequestByTokenSchema = z
  .object({
    id: z.string(),
    request_number: z.string(),
    service_type: z.string(),
    status: z.string(),
    property_city: z.string().nullable(),
    property_district: z.string().nullable(),
    property_type: z.string().nullable(),
  })
  .passthrough();

export type ServiceRequestByToken = z.infer<typeof serviceRequestByTokenSchema>;

/**
 * PUBLIC lead-capture input (`POST /tasks/public/lead`, unauthenticated).
 * The anonymous body names ONLY the listing, the lead kind and the visitor's
 * own contact fields — tenant linkage (company/property/unit), title,
 * description and priority are derived server-side from the active listing
 * row (fortress rule 1: derived, never received). Field rules mirror the Nest
 * `CreatePublicLeadDto` class-validator constraints one-for-one so the FE
 * boundary rejects exactly what the API would.
 */
export const createPublicLeadInputSchema = z.object({
  listing_id: z.string().uuid(),
  task_type: z.enum(["contract_request", "visit_request", "inquiry"]),
  visitor_name: z.string().trim().min(1).max(120),
  /** Digits with optional leading +, spaces or dashes (7–20 chars). */
  visitor_phone: z.string().regex(/^\+?[0-9][0-9\s-]{6,18}$/),
  visitor_email: z.string().email().max(254).nullish(),
  /** Free-text inquiry body (ContactDialog only). */
  message: z.string().max(2000).nullish(),
  preferred_visit_time: z.string().max(120).nullish(),
  /** Requested visit date (YYYY-MM-DD, must be a real calendar date). */
  due_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((d) => !Number.isNaN(new Date(d).getTime()), { message: "Invalid date" })
    .nullish(),
  /** Requested visit time (HH:mm, 24h). */
  due_time: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .nullish(),
});

export type CreatePublicLeadInput = z.infer<typeof createPublicLeadInputSchema>;
